import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react";
import {
  ChangeEvent,
  ChangeEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { useStore } from "../../contexts/store-provider";
import {
  getHeadquarterIdFromLocalStorage,
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import DropdownInput from "../../shared-components/input/DropdownInput";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import { formatMessage } from "../../translations/format-message";
import DailyTotallingTable from "./components/sales-details-component";
import { Field, Form, Formik } from "formik";
import _ from "lodash";
import FormikDateFieldMUI from "../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import { Button } from "react-bootstrap";
import { dailyTotallingFilterSchema } from "../../shared-components/FormikFormHandlers/FormikValidationSchema";
import CustomerToTreatmentProductDailySales from "./components/customer-to-treatment-product-daily-sales";
import CustomerDailySalesAccToAgeGroup from "./components/customer-daily-sales-acc-to-age-group-sales";
import FormikFormSelect from "../../shared-components/FormikFormHandlers/FormikFormSelect";
import { ISortingCustom } from "../../models/ISorting";
import './totalling-management.css';
import CustomDateInput from "../../shared-components/input/custom-date-input";
import moment from "moment";
import { getEffectiveDateTime } from "../../helpers/date-time-helper";

const TotallingManagement = () => {
  const { totallingManagementStore, masterManagementStore } = useStore();
  const {
    GetDailyTotallingSalesService,
    resetDailyTotallingSalesState,
    dailyTotallingSalesState,
    getDailyTotallingSales,
    setDailyTotallingFilterDetail,
    resetDailyTotallingFilterDetail,
    dailyTotallingFilter,
    GetCustomerToTreatmentProductDailySalesService,
    getCustomerToTreatmentProductDailySales,
    resetCustomerToTreatmentProductDailySalesState,
    customerToTreatmentProductDailySalesState,
    getCustomerSalesRatio,
    GetCustomerSalesRatioService,
    customerSalesRatioState,
    resetCustomerSalesRatioState
  } = totallingManagementStore;
  const { GetAllStoreOptionService, getAllStoreOptionsAll, GetAllStaffOptionServiceByStoreId, getAllStaffOptionsWithSelectStaff, staffOptionsState } =
    masterManagementStore;
  const userType = getUserTypeFromLocalStorage();
  let storeId = getStoreIdFromLocalStorage() || 0;
  let headquarterId = getHeadquarterIdFromLocalStorage() || 0;

  const [storeIdSearch, setStoreIdSearch] = useState<number>(
    userType === userTypeEnum[userTypeEnum.Store] ? storeId : -1
  );
  const [sorting, setSorting] = useState<ISortingCustom>({
      column: "time",
      orderBy: "asc",
  });

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const handleStoreIdSearch: ChangeEventHandler<HTMLSelectElement> =
    useCallback((event: ChangeEvent<HTMLSelectElement>) => {
      setStoreIdSearch(event.target.value as unknown as number);
    }, []);

  const callGetDailyTotallingSalesService = useCallback(() => {
    GetDailyTotallingSalesService(
      Number(storeIdSearch),
      headquarterId,
      dailyTotallingFilter
    );
    GetCustomerSalesRatioService(
      Number(storeIdSearch),
      headquarterId,
      dailyTotallingFilter.StaffId,
      dailyTotallingFilter.Date,
    );
  }, [
    GetDailyTotallingSalesService,
    GetCustomerSalesRatioService,
    storeIdSearch,
    headquarterId
  ]);

  const callGetCustomerToTreatmentProductDailySalesService = useCallback(() => {
    GetCustomerToTreatmentProductDailySalesService(
      Number(storeIdSearch),
      headquarterId,
      dailyTotallingFilter,
      sorting
    );
  }, [
     GetCustomerToTreatmentProductDailySalesService,
    storeIdSearch,
    headquarterId,
    sorting
  ]);

  useEffect(() => {
    if (userType === userTypeEnum[userTypeEnum.HeadQuarter])
      GetAllStoreOptionService();
    return () => {
      resetDailyTotallingSalesState();
      resetCustomerToTreatmentProductDailySalesState();
      resetDailyTotallingFilterDetail();
      resetCustomerSalesRatioState();
    };
  }, []);

  useEffect(() => {
    callGetDailyTotallingSalesService();
  }, [callGetDailyTotallingSalesService]);

  useEffect(() => {
    if(dailyTotallingFilter.isFilterChanged){
      GetCustomerToTreatmentProductDailySalesService(
        Number(storeIdSearch),
        headquarterId,
        dailyTotallingFilter,
        sorting
      );
      GetDailyTotallingSalesService(
        Number(storeIdSearch),
        headquarterId,
        dailyTotallingFilter
      );
      GetCustomerSalesRatioService(
        Number(storeIdSearch),
        headquarterId,
        dailyTotallingFilter.StaffId,
        dailyTotallingFilter.Date,
      );
      setDailyTotallingFilterDetail({...dailyTotallingFilter, isFilterChanged: false})
    }
  }, [dailyTotallingFilter.isFilterChanged]);

  useEffect(() => {
    callGetCustomerToTreatmentProductDailySalesService();
  }, [callGetCustomerToTreatmentProductDailySalesService]);
  
  useEffect(() => {
    GetAllStaffOptionServiceByStoreId(storeIdSearch, false, userType === userTypeEnum[userTypeEnum.HeadQuarter] ? headquarterId : null);
  }, [storeIdSearch]);

  return (
    <>
      {(dailyTotallingSalesState.inProgress ||
        customerToTreatmentProductDailySalesState.inProgress ||
        staffOptionsState.inProgress ||
        customerSalesRatioState.inProgress ||
        masterManagementStore.inProgress) && (
        <FormLoader
          loading={
            dailyTotallingSalesState.inProgress ||
            customerToTreatmentProductDailySalesState.inProgress ||
            staffOptionsState.inProgress ||
            customerSalesRatioState.inProgress ||
            masterManagementStore.inProgress
          }
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="daily_aggregate_sales" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="aggregate" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="daily_sales" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
              <li>
                {userType !== userTypeEnum[userTypeEnum.Store] && (
                  <>
                    {storeIdSearch && ( 
                      <div className="input-group">
                        <div className="input-group-text">
                          <span role="button" onClick={() => {}}>
                            <FontAwesomeIcon icon={faSearch} />
                          </span>
                        </div>
                        <DropdownInput
                          className="form-control"
                          placeholder={formatMessage("search_by_store_name")}
                          value={storeIdSearch}
                          onChange={(event)=>{
                            setDailyTotallingFilterDetail({...dailyTotallingFilter, StaffId: -1});
                            handleStoreIdSearch(event)
                          }}
                          options={getAllStoreOptionsAll.map((option) => ({
                            label: option.value,
                            value: option.id,
                          }))}
                        />
                      </div>
                    )}
                  </>
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-4 mb-2">
        <Formik
          initialValues={_.omit(dailyTotallingFilter, ["isFilterChanged", "isFilterActive"])}
          enableReinitialize={true}
          validationSchema={dailyTotallingFilterSchema}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            let tempStaffId = typeof values.StaffId === "string" ? parseInt(values.StaffId) : values.StaffId;
            let tempValues = {
              Date: values.Date,
              StaffId: tempStaffId 
            };
            setDailyTotallingFilterDetail({
              ...tempValues,
              isFilterActive: true,
              isFilterChanged: true,
              showClear: _.isEqual({
                  Date: getEffectiveDateTime(),
                  StaffId: -1,
                  showClear: false
                }, values) ? false : true
            });
          }}
        >
          {({ values, setFieldValue }) => {
            return (
              <Form className="user-form p-0 ">
                <div className="row">
                  <div className="col-md-4 col-sm-6 col-12 form-group">
                    {/* <Field
                      name="Date"
                      as={FormikDateFieldMUI}
                      disableFuture={true}
                      disable
                    /> */}
                    <CustomDateInput
                      className="form-control"
                      value={moment(values.Date).format("YYYY-MM-DD")}
                      onChange={(value: string) => setFieldValue("Date",value)}
                      disableFuture={true}
                    />
                  </div>
                  <div className="col-md-4 col-sm-6 col-12  form-group">
                    <Field
                      name="StaffId"
                      getData={false}
                      options={getAllStaffOptionsWithSelectStaff}
                      as={FormikFormSelect}
                    />
                  </div>
                  <div className="col-md-4 col-12 text-start">
                    <Button
                      type="submit"
                      className="btn btn-primary"
                    >
                      <FormattedMessage id="apply" />
                    </Button>
                    {dailyTotallingFilter.isFilterActive && dailyTotallingFilter.showClear && (
                      <Button
                        type="button"
                        disabled={dailyTotallingSalesState.inProgress}
                        className="ms-2"
                        variant="btn btn-secondary"
                        onClick={()=>{
                          setDailyTotallingFilterDetail({
                            Date: getEffectiveDateTime(),
                            StaffId: -1,
                            isFilterActive: false,
                            isFilterChanged: true,
                            showClear: false
                          })
                        }}
                      >
                        <FormattedMessage id="clear" />
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      <div className="totalling_tables_wrapper pb-4">
        <div className="mt-3">
          <h3 className="treatment_title"><FormattedMessage id="details_of_treatment_product_sales" /></h3>
          <DailyTotallingTable data={getDailyTotallingSales} />
        </div>
        <div className="mt-4">
          <h3 className="treatment_title"><FormattedMessage id="all_day_reservations" /></h3>
          <CustomerToTreatmentProductDailySales sorting={sorting} handleSortingChange={handleSortingChange} data={getCustomerToTreatmentProductDailySales} />
        </div>
        <div className="mt-4">
          <h3 className="treatment_title"><FormattedMessage id="by_gender_and_age_group" /></h3>
          <CustomerDailySalesAccToAgeGroup data={getCustomerSalesRatio} />
        </div>
      </div>
    </>
  );
};

export default observer(TotallingManagement);
